import { ParameterStore } from "@/components/ModelParameters/store/parameter-store";
import { validateRequired } from "@/components/SchemaEditor/validation";
import { ComponentType } from "@/queries/models/types";

export interface ParameterValidationErrors {
  name?: string;
  paramId?: string;
  placeholder?: string;
  options?: string;
  minMax?: string;
  groupParameters?: string;
}

const PARAM_ID_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

const validateParamId = (value: string | undefined, siblingIds: string[]): string | null => {
  const required = validateRequired(value, "Parameter key");
  if (required) {
    return required;
  }

  const trimmed = value!.trim();
  if (!PARAM_ID_PATTERN.test(trimmed)) {
    return "Parameter key must start with a letter or underscore and contain only letters, numbers, or underscores.";
  }

  if (siblingIds.includes(trimmed)) {
    return "Parameter key must be unique.";
  }

  return null;
};

const getFirstNestedError = (errors: ParameterValidationErrors): string | undefined =>
  errors.name ??
  errors.paramId ??
  errors.placeholder ??
  errors.options ??
  errors.minMax ??
  errors.groupParameters;

const validateParameterFields = (
  parameter: ParameterStore,
  siblingParamIds: string[],
): { isValid: boolean; errors: ParameterValidationErrors } => {
  const errors: ParameterValidationErrors = {};

  const nameError = validateRequired(parameter.name, "Parameter name");
  if (nameError) {
    errors.name = nameError;
  }

  const paramIdError = validateParamId(parameter.paramId, siblingParamIds);
  if (paramIdError) {
    errors.paramId = paramIdError;
  }

  switch (parameter.componentType) {
    case ComponentType.INPUT:
    case ComponentType.DROPDOWN:
    case ComponentType.MULTI_SELECT:
    case ComponentType.TAGS: {
      const placeholderError = validateRequired(parameter.placeholder, "Placeholder");
      if (placeholderError) {
        errors.placeholder = placeholderError;
      }
      break;
    }
    default:
      break;
  }

  if (
    parameter.componentType === ComponentType.DROPDOWN ||
    parameter.componentType === ComponentType.MULTI_SELECT
  ) {
    if (!parameter.options?.length) {
      errors.options = "At least one option is required.";
    }
  }

  const hasNumericBounds =
    parameter.componentType === ComponentType.SLIDER ||
    (parameter.componentType === ComponentType.INPUT && parameter.isNumericInput);

  if (hasNumericBounds) {
    const min = Number(parameter.min);
    const max = Number(parameter.max);

    if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
      errors.minMax = "Minimum must be less than or equal to maximum.";
    }
  }

  if (parameter.componentType === ComponentType.GROUP) {
    const children = parameter.groupParameters ?? [];

    if (children.length === 0) {
      errors.groupParameters = "Group must contain at least one parameter.";
    } else {
      for (const child of children) {
        const childSiblingIds = children
          .filter(candidate => candidate !== child)
          .map(candidate => candidate.paramId?.trim() ?? "")
          .filter(Boolean);

        const childResult = validateParameterFields(child, childSiblingIds);

        if (!childResult.isValid) {
          const nestedError = getFirstNestedError(childResult.errors);
          errors.groupParameters =
            nestedError ??
            `Invalid nested parameter "${child.name?.trim() || child.paramId?.trim() || "unnamed"}".`;
          break;
        }
      }
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const validateParameter = (
  parameter: ParameterStore,
  existingTopLevelParamIds: string[],
): { isValid: boolean; errors: ParameterValidationErrors } =>
  validateParameterFields(parameter, existingTopLevelParamIds);
